import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, Save, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Select, I18nInputs, ImageUpload } from './AdminUI';

const EMPTY = { category_key: '', key: '', label: {it:'',en:''}, desc: {it:'',en:''}, image: '', order: 0, active: true, hidden_in_cities: [] };

export default function AdminSubcategories() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState('');
  const [reordering, setReordering] = useState(false);

  const load = () => Promise.all([
    adminApi.subcategories(), adminApi.categories(), adminApi.cities()
  ]).then(([s, c, ci]) => { setItems(s); setCategories(c); setCities(ci); });
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await adminApi.updateSubcategory(editing.id, payload);
      else await adminApi.createSubcategory(payload);
      setEditing(null); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare la sottocategoria "${row.key}"?`)) return;
    await adminApi.deleteSubcategory(row.id); load();
  };

  const toggleVisibility = async (row) => {
    await adminApi.toggleSubcategoryVisibility(row.id); load();
  };

  const rows = useMemo(() => {
    const filtered = filterCat ? items.filter(i => i.category_key === filterCat) : items;
    return [...filtered].sort((a, b) => {
      if ((a.category_key || '') !== (b.category_key || '')) return (a.category_key || '').localeCompare(b.category_key || '');
      const oa = a.order ?? 0, ob = b.order ?? 0;
      if (oa !== ob) return oa - ob;
      return (a.key || '').localeCompare(b.key || '');
    });
  }, [items, filterCat]);

  const moveRow = async (row, dir) => {
    // Move only within the same category_key
    const sameCat = rows.filter(r => r.category_key === row.category_key);
    const localIdx = sameCat.findIndex(r => r.id === row.id);
    const j = localIdx + dir;
    if (j < 0 || j >= sameCat.length) return;
    const other = sameCat[j];
    setReordering(true);
    try {
      let oa = row.order ?? 0, ob = other.order ?? 0;
      if (oa === ob) { oa = localIdx; ob = j; }
      await Promise.all([
        adminApi.updateSubcategory(row.id, { ...row, order: ob }),
        adminApi.updateSubcategory(other.id, { ...other, order: oa }),
      ]);
      load();
    } catch (e) { alert('Errore riordinamento: ' + (e.response?.data?.detail || e.message)); }
    finally { setReordering(false); }
  };

  return (
    <div>
      <PageHeader title="Sottocategorie" subtitle={`${items.length} sottocategorie totali`} action={<button onClick={() => setEditing({ ...EMPTY, category_key: filterCat })} className="btn-primary flex items-center gap-2"><Plus size={16}/> Nuova</button>} />
      <div className="mb-4 flex gap-2">
        <Select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="max-w-xs">
          <option value="">Tutte le categorie</option>
          {categories.map(c => <option key={c.key} value={c.key}>{c.label?.it || c.key}</option>)}
        </Select>
      </div>
      <Table
        draggable
        onReorder={async (next) => {
          setItems(next);
          try {
            await adminApi.reorderSubcategories(next.map(x => x.id));
          } catch (e) { alert('Errore riordino: ' + (e.response?.data?.detail || e.message)); load(); }
        }}
        columns={[
          { key: 'category_key', label: 'Categoria', render: r => <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{r.category_key}</span> },
          { key: 'key', label: 'Key' },
          { key: 'label', label: 'Nome (IT)', render: r => <span className="font-semibold">{r.label?.it || r.label?.en || '—'}</span> },
          { key: 'image', label: 'Immagine', render: r => r.image ? <img src={r.image} alt="" className="w-16 h-10 object-cover rounded"/> : '—' },
          { key: 'order', label: '#' },
          { key: 'active', label: 'Stato', render: r => (
            <button
              onClick={() => toggleVisibility(r)}
              data-testid={`sub-toggle-${r.id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${r.active !== false ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}
              title={r.active !== false ? 'Clicca per nascondere' : 'Clicca per rendere visibile'}
            >
              {r.active !== false ? <><Eye size={12}/> Attiva</> : <><EyeOff size={12}/> Nascosta</>}
            </button>
          )}
        ]}
        rows={rows}
        actions={row => {
          const sameCat = rows.filter(r => r.category_key === row.category_key);
          const idx = sameCat.findIndex(r => r.id === row.id);
          return (
          <div className="flex gap-1 justify-end items-center">
            <button onClick={() => moveRow(row, -1)} disabled={reordering || idx <= 0} title="Sposta su (nella categoria)" className="p-1.5 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"><ArrowUp size={14}/></button>
            <button onClick={() => moveRow(row, 1)} disabled={reordering || idx >= sameCat.length - 1} title="Sposta giù (nella categoria)" className="p-1.5 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"><ArrowDown size={14}/></button>
            <button onClick={() => setEditing({...row})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600"><Edit2 size={15}/></button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 size={15}/></button>
          </div>
        );}}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica sottocategoria' : 'Nuova sottocategoria'}>
        {editing && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Categoria padre">
                <Select required value={editing.category_key} onChange={e => setEditing({...editing, category_key: e.target.value})}>
                  <option value="">-- seleziona --</option>
                  {categories.map(c => <option key={c.key} value={c.key}>{c.label?.it || c.key}</option>)}
                </Select>
              </Field>
              <Field label="Key" hint="es. stellati"><Input required disabled={!!editing.id} value={editing.key} onChange={e => setEditing({...editing, key: e.target.value})} /></Field>
            </div>
            <Field label="Nome (per lingua)"><I18nInputs value={editing.label} onChange={v => setEditing({...editing, label: v})} /></Field>
            <Field label="Descrizione (per lingua)"><I18nInputs kind="textarea" value={editing.desc} onChange={v => setEditing({...editing, desc: v})} /></Field>
            <Field label="Immagine"><ImageUpload value={editing.image} onChange={v => setEditing({...editing, image: v})} /></Field>
            <Field label="Ordine"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})} /></Field>

            {/* Per-city visibility control */}
            <div className="mt-2 mb-4 rounded-lg border-2 border-amber-200 bg-amber-50/60 p-3" data-testid="hidden-cities-block">
              <div className="text-sm font-semibold text-amber-900 mb-1">🏙️ Visibilità per città</div>
              <p className="text-xs text-amber-800/80 mb-2">
                Seleziona le città in cui questa sottocategoria deve essere <b>NASCOSTA</b>. Lascia vuoto per mostrarla ovunque.
              </p>
              <div className="flex flex-wrap gap-2">
                {cities.map(city => {
                  const hidden = (editing.hidden_in_cities || []).includes(city.slug);
                  return (
                    <button
                      key={city.slug}
                      type="button"
                      onClick={() => {
                        const cur = editing.hidden_in_cities || [];
                        const next = hidden ? cur.filter(s => s !== city.slug) : [...cur, city.slug];
                        setEditing({ ...editing, hidden_in_cities: next });
                      }}
                      data-testid={`hide-city-${city.slug}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                        hidden
                          ? 'border-red-500 bg-red-100 text-red-800'
                          : 'border-green-400 bg-green-50 text-green-800 hover:bg-green-100'
                      }`}
                    >
                      {hidden ? '🚫' : '✅'} {city.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
